import React, { useState, useEffect, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPaperPlane, faCheckCircle,
  faExclamationTriangle, faTrash, faRotateRight,
  faClock, faEnvelope, faUserPlus, faLayerGroup,
  faCircleQuestion, faBan, faCopy,
} from "@fortawesome/free-solid-svg-icons";
import ModalHeader from "./ModalHeader";
import api from "../services/api";
import styles from "./InviteStudentModal.module.css";
import { useBodyScrollLock } from "../hooks/useBodyScrollLock";

// Ícone do WhatsApp via SVG inline — o projeto não tem
// @fortawesome/free-brands-svg-icons instalado, e não vale a pena puxar o
// pacote inteiro só por este glifo.
function WhatsappIcon() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true">
      <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.46 1.32 4.96L2.05 22l5.25-1.38a9.87 9.87 0 0 0 4.74 1.21h.01c5.46 0 9.9-4.45 9.9-9.91 0-2.65-1.03-5.14-2.9-7.01A9.82 9.82 0 0 0 12.04 2Zm0 1.67c2.2 0 4.26.86 5.82 2.42a8.2 8.2 0 0 1 2.41 5.82c0 4.54-3.7 8.24-8.24 8.24a8.2 8.2 0 0 1-4.19-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.18 8.18 0 0 1-1.26-4.38c0-4.54 3.7-8.24 8.25-8.24Zm-4.52 4.3c-.17 0-.44.06-.67.32-.23.25-.87.86-.87 2.08 0 1.22.9 2.4 1.02 2.57.12.17 1.75 2.67 4.25 3.74.6.26 1.06.41 1.42.53.6.19 1.14.16 1.57.1.48-.07 1.47-.6 1.68-1.18.21-.58.21-1.08.14-1.18-.06-.1-.23-.16-.48-.28-.25-.13-1.47-.73-1.7-.81-.23-.08-.39-.13-.56.13-.17.25-.64.81-.78.97-.14.17-.29.19-.54.06-.25-.13-1.04-.38-1.98-1.22-.73-.65-1.23-1.46-1.37-1.7-.14-.25-.02-.39.11-.51.11-.11.25-.29.37-.44.12-.14.16-.25.24-.41.08-.17.04-.31-.02-.44-.06-.13-.56-1.36-.77-1.86-.2-.48-.4-.42-.56-.42Z" />
    </svg>
  );
}

const STATUS_LABEL = {
  pending:  { label: "Pendente",  color: "amber" },
  accepted: { label: "Aceito",    color: "green"  },
  expired:  { label: "Expirado",  color: "muted"  },
};

const BULK_MAX_ROWS = 150;

const BULK_RESULT_META = {
  created:         { label: "Cadastrado",       color: "green", icon: faCheckCircle },
  already_student: { label: "Já é seu aluno",   color: "muted", icon: faCheckCircle },
  needs_review:    { label: "Revisar manualmente", color: "amber", icon: faCircleQuestion },
  already_linked:  { label: "Já vinculado",     color: "red",   icon: faBan },
  duplicate:       { label: "Repetido na lista", color: "muted", icon: faCopy },
  error:           { label: "Erro",             color: "red",   icon: faExclamationTriangle },
};

// Aceita "Nome, Email" (colado do Excel/Sheets, vírgula ou tab entre colunas)
// ou só "Email" por linha — nesse caso o nome é inferido da parte antes do @.
function parseBulkLines(text) {
  return text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line, idx) => {
      let name = "";
      let email = "";
      if (line.includes(",")) {
        const [n, ...rest] = line.split(",");
        name = n.trim();
        email = rest.join(",").trim();
      } else if (line.includes("\t")) {
        const [n, e] = line.split("\t");
        name = (n || "").trim();
        email = (e || "").trim();
      } else {
        email = line.trim();
      }
      const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
      if (!name && emailValid) {
        name = email
          .split("@")[0]
          .replace(/[._-]+/g, " ")
          .replace(/\b\w/g, (c) => c.toUpperCase());
      }
      return {
        key: idx,
        raw: line,
        name,
        email: email.toLowerCase(),
        valid: emailValid && !!name,
      };
    });
}

// Monta a mensagem pré-formatada e o link wa.me com as credenciais do
// convite. Sem número de telefone do aluno cadastrado no sistema — abre o
// WhatsApp com o texto pronto e o professor escolhe o contato manualmente
// (Web/App). Sem nome da plataforma na mensagem — o convite é do
// professor, não da marca por trás do app (branding fica só pro professor).
// O link (Acesse: ...) já sai como texto puro começando com "https://" —
// o WhatsApp auto-detecta e transforma em link clicável sozinho ao
// entregar a mensagem; não dá pra customizar o texto do link (WhatsApp não
// suporta markdown de hyperlink, só *negrito*/_itálico_/~tachado~).
function buildWhatsappInviteUrl({ name, email, password, professorName }) {
  const appUrl = window.location.origin;
  const firstName = (name || "").trim().split(/\s+/)[0] || "";
  const greeting = firstName ? `Oi, ${firstName}!` : "Oi!";
  const profName = (professorName || "").trim() || "Seu professor";
  const text = [
    `${greeting} 🎉 Seu professor ${profName} acaba de criar sua conta.`,
    "",
    `Acesse: ${appUrl}`,
    `Email: ${email}`,
    `Senha temporária: ${password}`,
    "",
    "No primeiro acesso você vai precisar trocar a senha. Qualquer dúvida, me chama por aqui!",
  ].join("\n");
  return `https://wa.me/?text=${encodeURIComponent(text)}`;
}

function InviteRow({ inv, onCancel, confirmingId, onConfirmDelete, onAbortDelete, professorName }) {
  const meta = STATUS_LABEL[inv.status] || STATUS_LABEL.expired;
  const isConfirming = confirmingId === inv.id;
  // temp_password só vem preenchida da API enquanto o convite está pending
  // (ver InviteOut/_invite_out no backend) — funciona pra qualquer convite
  // pendente da lista, não só o que acabou de ser enviado nesta sessão do
  // modal (sobrevive a fechar/reabrir o modal, enquanto o convite não for
  // aceito ou cancelado).
  const showWhatsapp = inv.status === "pending" && !!inv.temp_password;
  return (
    <div className={styles.inviteRow}>
      <div className={styles.inviteInfo}>
        <span className={styles.inviteName}>{inv.name}</span>
        <span className={styles.inviteEmail}>{inv.email}</span>
      </div>
      {isConfirming ? (
        <div className={styles.deleteConfirm}>
          <span className={styles.deleteConfirmText}>Cancelar convite?</span>
          <button className={styles.deleteConfirmYes} onClick={onConfirmDelete} title="Confirmar">
            Sim
          </button>
          <button className={styles.deleteConfirmNo} onClick={onAbortDelete} title="Voltar">
            Não
          </button>
        </div>
      ) : (
        <>
          <span className={`${styles.statusBadge} ${styles[`status_${meta.color}`]}`}>
            {meta.label}
          </span>
          {showWhatsapp && (
            <a
              className={`${styles.whatsappBtn} touchTarget`}
              href={buildWhatsappInviteUrl({
                name: inv.name,
                email: inv.email,
                password: inv.temp_password,
                professorName,
              })}
              target="_blank"
              rel="noopener noreferrer"
              title="Enviar email, senha e link por WhatsApp"
              aria-label="Enviar credenciais do convite por WhatsApp"
            >
              <WhatsappIcon />
            </a>
          )}
          {inv.status === "pending" && (
            <button
              className={styles.cancelBtn}
              onClick={() => onCancel(inv.id)}
              title="Cancelar convite"
            >
              <FontAwesomeIcon icon={faTrash} />
            </button>
          )}
        </>
      )}
    </div>
  );
}

function BulkResultRow({ result }) {
  const meta = BULK_RESULT_META[result.status] || BULK_RESULT_META.error;
  return (
    <div className={styles.inviteRow}>
      <div className={styles.inviteInfo}>
        <span className={styles.inviteName}>{result.name || result.email}</span>
        <span className={styles.inviteEmail}>{result.message}</span>
      </div>
      <span className={`${styles.statusBadge} ${styles[`status_${meta.color}`]}`}>
        <FontAwesomeIcon icon={meta.icon} /> {meta.label}
      </span>
    </div>
  );
}


export default function InviteStudentModal({ open, onClose, professorLogoUrl, professorBrandColor, professorName }) {
  const [name, setName]           = useState("");
  useBodyScrollLock(open);
  const [email, setEmail]         = useState("");
  const [sending, setSending]     = useState(false);
  const [success, setSuccess]     = useState(null);
  const [error, setError]         = useState(null);
  const [invites, setInvites]     = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  // Confirmação para usuário já existente
  const [confirmData, setConfirmData] = useState(null); // { message, existingName }
  // Confirmação para cancelar convite
  const [cancelConfirmId, setCancelConfirmId] = useState(null);

  // ── Carga em massa ──
  const [mode, setMode]                 = useState("single"); // "single" | "bulk"
  const [bulkText, setBulkText]         = useState("");
  const [bulkSending, setBulkSending]   = useState(false);
  const [bulkResults, setBulkResults]   = useState(null); // resultado da última chamada
  const [bulkError, setBulkError]       = useState(null);

  const bulkRows = bulkText.trim() ? parseBulkLines(bulkText) : [];
  const bulkValidRows = bulkRows.filter((r) => r.valid);
  const bulkInvalidCount = bulkRows.length - bulkValidRows.length;
  const bulkOverLimit = bulkValidRows.length > BULK_MAX_ROWS;

  const loadInvites = useCallback(async () => {
    setLoadingList(true);
    try {
      const res = await api.get("/teacher/invites");
      setInvites(res.data);
    } catch (_) {
      // silencioso
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => {
    if (open) { loadInvites(); }
  }, [open, loadInvites]);

  const handleSubmit = async (e, confirm = false) => {
    if (e?.preventDefault) e.preventDefault();
    if (!name.trim() || !email.trim()) return;
    setSending(true);
    setError(null);
    setSuccess(null);
    setConfirmData(null);
    try {
      const res = await api.post("/teacher/students/invite", {
        name: name.trim(),
        email: email.trim(),
        confirm,
      });
      const { status, message } = res.data;
      if (status === "user_exists") {
        // Pausar e pedir confirmação ao professor
        setConfirmData({ message, existingName: res.data.existing_name });
      } else if (status === "already_student") {
        setError(message);
      } else if (status === "already_linked") {
        setError(message);
      } else if (status === "created") {
        setSuccess(message || "Conta criada e credenciais enviadas por email!");
        setName("");
        setEmail("");
        loadInvites();
      } else {
        // linked | fallback
        setSuccess(message || "Aluno adicionado com sucesso!");
        setName("");
        setEmail("");
        loadInvites();
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === "object") {
        setError(detail.message || "Erro ao enviar convite.");
      } else {
        setError(detail || "Erro ao enviar convite.");
      }
    } finally {
      setSending(false);
    }
  };

  const handleConfirm = () => handleSubmit(null, true);
  const handleCancelConfirm = () => { setConfirmData(null); };

  const handleCancel = (inviteId) => {
    setCancelConfirmId(inviteId);
  };

  const handleCancelConfirmed = async () => {
    const inviteId = cancelConfirmId;
    setCancelConfirmId(null);
    try {
      await api.delete(`/teacher/invites/${inviteId}`);
      loadInvites();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "object" ? detail.message : detail || "Erro ao cancelar convite.");
    }
  };

  const handleBulkSubmit = async () => {
    if (!bulkValidRows.length || bulkOverLimit) return;
    setBulkSending(true);
    setBulkError(null);
    try {
      const res = await api.post("/teacher/students/bulk-create", {
        students: bulkValidRows.map((r) => ({ name: r.name, email: r.email })),
      });
      setBulkResults(res.data.results || []);
      setBulkText("");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setBulkError(typeof detail === "object" ? detail.message : detail || "Erro ao importar alunos.");
    } finally {
      setBulkSending(false);
    }
  };

  const handleBulkReset = () => {
    setBulkResults(null);
    setBulkError(null);
    setBulkText("");
  };

  if (!open) return null;

  return (
    <div className={styles.overlay} role="dialog" aria-modal="true">
      <div className={styles.modal}>
        <ModalHeader
          title="Adicionar Aluno"
          subtitle={mode === "bulk" ? "Cadastre vários alunos de uma vez" : "Envie um convite por email para o aluno entrar na sua turma"}
          onClose={onClose}
          logoUrl={professorLogoUrl}
          brandColor={professorBrandColor}
        />

        <div className={styles.body}>

          {/* Alternância: um por vez vs. em massa */}
          <div className={styles.tabs}>
            <button
              type="button"
              className={`${styles.tabBtn} ${mode === "single" ? styles.tabBtnActive : ""}`}
              onClick={() => setMode("single")}
              style={mode === "single" && professorBrandColor ? { borderColor: professorBrandColor, color: professorBrandColor } : undefined}
            >
              <FontAwesomeIcon icon={faUserPlus} /> Um por vez
            </button>
            <button
              type="button"
              className={`${styles.tabBtn} ${mode === "bulk" ? styles.tabBtnActive : ""}`}
              onClick={() => setMode("bulk")}
              style={mode === "bulk" && professorBrandColor ? { borderColor: professorBrandColor, color: professorBrandColor } : undefined}
            >
              <FontAwesomeIcon icon={faLayerGroup} /> Em massa
            </button>
          </div>

          <p className={styles.modeHint}>
            {mode === "single" ? (
              <>
                <strong>Um por vez</strong> manda um convite por email - o aluno escolhe a
                própria senha ao aceitar. Prefira <strong>Em massa</strong> se já tem a lista
                de alunos pronta.
              </>
            ) : (
              <>
                <strong>Em massa</strong> cadastra a conta na hora, com senha temporária
                enviada por email - sem convite pra aceitar. Prefira <strong>Um por vez</strong>{" "}
                pra convidar só um aluno.
              </>
            )}
          </p>

          {mode === "single" && (
          <>
          {/* Confirmação: usuário já existe */}
          {confirmData && (
            <div className={styles.confirmBox} style={professorBrandColor ? { borderColor: professorBrandColor + "55" } : undefined}>
              <p className={styles.confirmMsg}>{confirmData.message}</p>
              <div className={styles.confirmActions}>
                <button
                  type="button"
                  className={styles.confirmYes}
                  onClick={handleConfirm}
                  disabled={sending}
                  style={professorBrandColor ? { background: professorBrandColor } : undefined}
                >
                  {sending ? "Adicionando…" : "Sim, adicionar"}
                </button>
                <button type="button" className={styles.confirmNo} onClick={handleCancelConfirm}>
                  Cancelar
                </button>
              </div>
            </div>
          )}

          {/* Formulário */}
          <form onSubmit={handleSubmit} className={styles.form}>
            <div className={styles.field}>
              <label htmlFor="inv-name" className={styles.label}>Nome do aluno</label>
              <input
                id="inv-name"
                className={styles.input}
                type="text"
                placeholder="Ex.: João Silva"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                autoComplete="off"
              />
            </div>
            <div className={styles.field}>
              <label htmlFor="inv-email" className={styles.label}>Email do aluno</label>
              <input
                id="inv-email"
                className={styles.input}
                type="email"
                placeholder="joao@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="off"
              />
            </div>

            {success && (
              <div
                className={styles.feedbackSuccess}
                style={professorBrandColor ? {
                  background: professorBrandColor + "22",
                  borderColor: professorBrandColor + "55",
                  color: professorBrandColor,
                } : undefined}
              >
                <FontAwesomeIcon icon={faCheckCircle} /> {success}
              </div>
            )}
            {error && (
              <div className={styles.feedbackError}>
                <FontAwesomeIcon icon={faExclamationTriangle} /> {error}
              </div>
            )}

            <button
              type="submit"
              className={styles.sendBtn}
              disabled={sending}
              style={professorBrandColor ? { background: professorBrandColor } : undefined}
            >
              <FontAwesomeIcon icon={faPaperPlane} />
              {sending ? "Enviando…" : "Enviar convite"}
            </button>
          </form>

          {/* Lista de convites enviados */}
          <div className={styles.listSection}>
            <div className={styles.listHeader}>
              <span className={styles.listTitle}>
                <FontAwesomeIcon icon={faEnvelope} /> Convites enviados
              </span>
              <button className={styles.refreshBtn} onClick={loadInvites} title="Atualizar">
                <FontAwesomeIcon icon={faRotateRight} className={loadingList ? styles.spin : ""} />
              </button>
            </div>

            {invites.length === 0 ? (
              <div className={styles.emptyState}>
                <FontAwesomeIcon icon={faClock} />
                <span>Nenhum convite enviado ainda.</span>
              </div>
            ) : (
              <div className={styles.inviteList}>
                {invites.map((inv) => (
                  <InviteRow
                    key={inv.id}
                    inv={inv}
                    onCancel={handleCancel}
                    confirmingId={cancelConfirmId}
                    onConfirmDelete={handleCancelConfirmed}
                    onAbortDelete={() => setCancelConfirmId(null)}
                    professorName={professorName}
                  />
                ))}
              </div>
            )}
          </div>
          </>
          )}

          {mode === "bulk" && (
          <div className={styles.bulkWrap}>
            <p className={styles.bulkHint}>
              Já tem seus alunos? Cole uma linha por aluno abaixo, no formato
              <strong> Nome, Email</strong> - direto do Excel/Sheets. Cada um recebe um
              email com a senha de acesso (sem convite pra aceitar, a conta já entra ativa).
            </p>

            {!bulkResults && (
              <>
                <textarea
                  className={styles.bulkTextarea}
                  placeholder={"João Silva, joao@email.com\nMaria Souza, maria@email.com"}
                  value={bulkText}
                  onChange={(e) => setBulkText(e.target.value)}
                  rows={6}
                />

                {bulkRows.length > 0 && (
                  <div className={styles.bulkPreview}>
                    <div className={styles.bulkPreviewHeader}>
                      <span>{bulkValidRows.length} aluno{bulkValidRows.length === 1 ? "" : "s"} pronto{bulkValidRows.length === 1 ? "" : "s"}</span>
                      {bulkInvalidCount > 0 && (
                        <span className={styles.bulkInvalidNote}>
                          <FontAwesomeIcon icon={faExclamationTriangle} /> {bulkInvalidCount} linha{bulkInvalidCount === 1 ? "" : "s"} inválida{bulkInvalidCount === 1 ? "" : "s"} (ignorada{bulkInvalidCount === 1 ? "" : "s"})
                        </span>
                      )}
                    </div>
                    <div className={styles.bulkPreviewList}>
                      {bulkRows.map((r) => (
                        <div key={r.key} className={`${styles.bulkPreviewRow} ${r.valid ? "" : styles.bulkPreviewRowInvalid}`}>
                          <FontAwesomeIcon icon={r.valid ? faCheckCircle : faExclamationTriangle} />
                          <span className={styles.bulkPreviewText}>
                            {r.valid ? `${r.name} · ${r.email}` : `Não entendi: "${r.raw}"`}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {bulkOverLimit && (
                  <div className={styles.feedbackError}>
                    <FontAwesomeIcon icon={faExclamationTriangle} />
                    Máximo de {BULK_MAX_ROWS} alunos por importação. Divida em lotes menores.
                  </div>
                )}
                {bulkError && (
                  <div className={styles.feedbackError}>
                    <FontAwesomeIcon icon={faExclamationTriangle} /> {bulkError}
                  </div>
                )}

                <button
                  type="button"
                  className={styles.sendBtn}
                  disabled={!bulkValidRows.length || bulkOverLimit || bulkSending}
                  onClick={handleBulkSubmit}
                  style={professorBrandColor ? { background: professorBrandColor } : undefined}
                >
                  <FontAwesomeIcon icon={faLayerGroup} />
                  {bulkSending ? "Cadastrando…" : `Cadastrar ${bulkValidRows.length || ""} aluno${bulkValidRows.length === 1 ? "" : "s"}`}
                </button>
              </>
            )}

            {bulkResults && (
              <>
                <div className={styles.feedbackSuccess} style={professorBrandColor ? {
                  background: professorBrandColor + "22", borderColor: professorBrandColor + "55", color: professorBrandColor,
                } : undefined}>
                  <FontAwesomeIcon icon={faCheckCircle} />
                  {bulkResults.filter((r) => r.status === "created").length} de {bulkResults.length} cadastrados.
                </div>
                <div className={styles.inviteList}>
                  {bulkResults.map((r, i) => (
                    <BulkResultRow key={`${r.email}-${i}`} result={r} />
                  ))}
                </div>
                <button type="button" className={styles.confirmNo} onClick={handleBulkReset}>
                  Nova importação
                </button>
              </>
            )}
          </div>
          )}
        </div>
      </div>
    </div>
  );
}
